import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Collapse,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  Mic as MicIcon,
  Favorite as FavoriteIcon,
  Description as DescriptionIcon,
  Inbox as InboxIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  CalendarToday as CalendarIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  LocationOn as LocationOnIcon,
  ArrowForward as ArrowForwardIcon,
  Topic as TopicIcon,
  Person as PersonIcon,
  Folder as FolderIcon,
  AllInclusive as AllIcon,
} from '@mui/icons-material';
import { ResponsiveAdminLayout } from '../components/ResponsiveAdminLayout';
import { Pagination } from '../components/Pagination';
import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../hooks/useAuth';
import { AppColors } from '../theme/colors';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { AuditLogService } from '../services/auditLogService';
import { AuditLog } from '../types';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { getBoxTemplate } from '../constants/industryTemplates';

// AuditLog型を使用（types/index.tsで定義済み）

export const AuditLogPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { company, loading: companyLoading } = useCompany(user?.uid || null);
  const isDesktop = useIsDesktop();
  
  const [isSystemAdminExpanded, setIsSystemAdminExpanded] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<string | null>('all');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [expandedContent, setExpandedContent] = useState<{ [key: string]: boolean }>({});
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [userNames, setUserNames] = useState<{ [userId: string]: string }>({});
  const [fullContentCache, setFullContentCache] = useState<{ [key: string]: string }>({});

  // ページネーション用
  const ITEMS_PER_PAGE = 10;
  
  // URLパラメータからページ番号を取得
  const currentPage = useMemo(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  }, [searchParams]);
  
  // ページ番号を変更する関数
  const setCurrentPage = useCallback((page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page === 1) {
      params.delete('page');
    } else {
      params.set('page', page.toString());
    }
    navigate(`/admin/audit-log?${params.toString()}`, { replace: true });
  }, [searchParams, navigate]);

  // ページネーション適用後の監査ログリスト
  const paginatedAuditLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return auditLogs.slice(startIndex, endIndex);
  }, [auditLogs, currentPage]);

  // 総ページ数
  const totalPages = useMemo(() => {
    return Math.ceil(auditLogs.length / ITEMS_PER_PAGE);
  }, [auditLogs.length]);
  
  // 完全な投稿内容を取得する関数
  const getFullContent = async (postId: string, currentContent: string): Promise<string> => {
    // 既に完全な内容の場合はそのまま返す
    if (!currentContent.endsWith('...') || currentContent.length > 103) {
      return currentContent;
    }
    
    try {
      const { db } = await import('../firebase/config');
      const { doc, getDoc } = await import('firebase/firestore');
      
      const postDoc = await getDoc(doc(db, 'posts', postId));
      if (postDoc.exists()) {
        const postData = postDoc.data();
        return postData.content || currentContent;
      }
    } catch (error) {
      console.error('投稿内容取得エラー:', error);
    }
    return currentContent;
  };
  const [fullPostContent, setFullPostContent] = useState<{ [key: string]: string }>({});

  // フィルター変更時にページをリセット
  const prevFiltersRef = useRef<{ selectedFilter: string | null; selectedDate: Date | null } | null>(null);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    if (company?.id) {
      fetchAuditLogs();
    }
  }, [company?.id, selectedFilter, selectedDate]);

  // フィルター変更時に1ページ目に戻す
  useEffect(() => {
    // 初回マウント時はスキップ
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevFiltersRef.current = { selectedFilter, selectedDate };
      return;
    }

    const prevFilters = prevFiltersRef.current;
    if (!prevFilters) {
      prevFiltersRef.current = { selectedFilter, selectedDate };
      return;
    }

    const filtersChanged =
      prevFilters.selectedFilter !== selectedFilter ||
      prevFilters.selectedDate !== selectedDate;

    if (filtersChanged) {
      const params = new URLSearchParams(window.location.search);
      const currentPageParam = params.get('page');
      if (currentPageParam && currentPageParam !== '1') {
        params.delete('page');
        navigate(`/admin/audit-log?${params.toString()}`, { replace: true });
      }
    }

    prevFiltersRef.current = { selectedFilter, selectedDate };
  }, [selectedFilter, selectedDate, navigate]);

  const fetchAuditLogs = async () => {
    if (!company?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const logs = await AuditLogService.getAuditLogs(
        company.id,
        selectedFilter || undefined,
        selectedDate || undefined
      );
      setAuditLogs(logs);
      
      // ユーザー名を取得してキャッシュ
      const uniqueUserIds = [...new Set(logs.map(log => log.userId))];
      const userNamePromises = uniqueUserIds.map(async (userId) => {
        const userName = await getUserName(userId);
        return { userId, userName };
      });
      
      const userNameResults = await Promise.all(userNamePromises);
      const newUserNames: { [userId: string]: string } = {};
      userNameResults.forEach(({ userId, userName }) => {
        newUserNames[userId] = userName;
      });
      
      setUserNames(prev => ({ ...prev, ...newUserNames }));
    } catch (err: any) {
      setError(err.message || '監査ログの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };


  const clearDate = () => {
    setSelectedDate(null);
  };

  const handleDatePickerOpen = () => {
    setDatePickerOpen(true);
  };

  const handleDatePickerClose = () => {
    setDatePickerOpen(false);
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    setDatePickerOpen(false);
  };

  // ユーザー名を取得してキャッシュする関数（Flutter版の_getUserNameと同等）
  const getUserName = async (userId: string): Promise<string> => {
    // キャッシュから取得
    if (userNames[userId]) {
      return userNames[userId];
    }

    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      let userName = '不明';
      if (userDoc.exists()) {
        const userData = userDoc.data();
        userName = userData.displayName || userData.email || '不明';
      }
      
      // キャッシュに保存
      setUserNames(prev => ({
        ...prev,
        [userId]: userName
      }));
      
      return userName;
    } catch (error) {
      console.error('ユーザー名取得エラー:', error);
      return '不明';
    }
  };

  const toggleExpanded = (contentId: string) => {
    setExpandedContent(prev => ({
      ...prev,
      [contentId]: !prev[contentId]
    }));
  };

  // 完全な投稿内容を取得
  const fetchFullPostContent = async (postId: string, postType: string) => {
    if (fullPostContent[postId]) {
      return fullPostContent[postId];
    }

    try {
      const { db } = await import('../firebase/config');
      const { doc, getDoc } = await import('firebase/firestore');
      
      const postDoc = await getDoc(doc(db, 'posts', postId));
      if (postDoc.exists()) {
        const postData = postDoc.data();
        const content = postData.content || '';
        setFullPostContent(prev => ({
          ...prev,
          [postId]: content
        }));
        return content;
      }
    } catch (error) {
      console.error('投稿内容取得エラー:', error);
    }
    return '';
  };

  // 投稿タイプのラベルを取得
  const getPostTypeLabel = (postType?: string): string => {
    if (postType === 'voice' || postType === 'thanks' || postType === 'theme') {
      return getBoxTemplate(company?.industryType, postType).name;
    }
    return '投稿';
  };

  const buildDescriptionWithColoredStatus = (description: string, details: any) => {
    // メモ投稿の場合は説明文のみ表示（内容は別のセクションで表示）
    if (details.actionType === 'memo_added') {
      const postTypeLabel = getPostTypeLabel(details.postType);
      return (
        <Typography variant="body2" sx={{ fontWeight: 500, color: AppColors.textPrimary }}>
          {postTypeLabel}にメモを書き込みました
        </Typography>
      );
    }

    if (details.actionType === 'status_change' || details.actionType === 'visibility_change') {
      const oldStatusLabel = details.oldStatusLabel || details.oldStatus || '不明';
      const newStatusLabel = details.newStatusLabel || details.newStatus || '不明';
      
      // 新しい形式 [ステータス] → [ステータス] の場合
      if (description.includes('[') && description.includes(']')) {
        const parts = description.split('[');
        if (parts.length >= 3) {
          const beforeFirstStatus = parts[0];
          const firstStatus = parts[1].split(']')[0];
          const middle = parts[1].split(']')[1];
          const secondStatus = parts[2].split(']')[0];
          const afterSecondStatus = parts[2].split(']').length > 1 ? parts[2].split(']')[1] : '';
          
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: AppColors.textPrimary }}>
                {beforeFirstStatus}
              </Typography>
              <Chip
                label={firstStatus}
                size="small"
                sx={{
                  backgroundColor: AuditLogService.getStatusBackgroundColor(firstStatus) + '26',
                  color: AuditLogService.getStatusTextColor(firstStatus),
                  border: `1px solid ${AuditLogService.getStatusBackgroundColor(firstStatus) + '4D'}`,
                  fontWeight: 500,
                }}
              />
              <ArrowForwardIcon sx={{ fontSize: 16, color: AppColors.textSecondary }} />
              <Chip
                label={secondStatus}
                size="small"
                sx={{
                  backgroundColor: AuditLogService.getStatusBackgroundColor(secondStatus) + '26',
                  color: AuditLogService.getStatusTextColor(secondStatus),
                  border: `1px solid ${AuditLogService.getStatusBackgroundColor(secondStatus) + '4D'}`,
                  fontWeight: 500,
                }}
              />
              {afterSecondStatus && (
                <Typography variant="body2" sx={{ fontWeight: 500, color: AppColors.textPrimary }}>
                  {afterSecondStatus}
                </Typography>
              )}
            </Box>
          );
        }
      }
      
      // 古い形式 「ステータス」から「ステータス」に変更 の場合
      if (description.includes('「') && description.includes('」')) {
        const parts = description.split('「');
        if (parts.length >= 3) {
          const beforeFirstStatus = parts[0];
          const firstStatus = parts[1].split('」')[0];
          const middle = parts[1].split('」')[1];
          const secondStatus = parts[2].split('」')[0];
          const afterSecondStatus = parts[2].split('」').length > 1 ? parts[2].split('」')[1] : '';
          
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: AppColors.textPrimary }}>
                {beforeFirstStatus}
              </Typography>
              <Chip
                label={firstStatus}
                size="small"
                sx={{
                  backgroundColor: AuditLogService.getStatusBackgroundColor(firstStatus) + '26',
                  color: AuditLogService.getStatusTextColor(firstStatus),
                  border: `1px solid ${AuditLogService.getStatusBackgroundColor(firstStatus) + '4D'}`,
                  fontWeight: 500,
                }}
              />
              <ArrowForwardIcon sx={{ fontSize: 16, color: AppColors.textSecondary }} />
              <Chip
                label={secondStatus}
                size="small"
                sx={{
                  backgroundColor: AuditLogService.getStatusBackgroundColor(secondStatus) + '26',
                  color: AuditLogService.getStatusTextColor(secondStatus),
                  border: `1px solid ${AuditLogService.getStatusBackgroundColor(secondStatus) + '4D'}`,
                  fontWeight: 500,
                }}
              />
              {afterSecondStatus && (
                <Typography variant="body2" sx={{ fontWeight: 500, color: AppColors.textPrimary }}>
                  {afterSecondStatus}
                </Typography>
              )}
            </Box>
          );
        }
      }
    }
    
    // 通常の説明文の場合はそのまま表示
    return (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {description}
      </Typography>
    );
  };

  const buildExpandableContent = (content: string, contentId: string, postId?: string, isMemoContent?: boolean) => {
    const isExpanded = expandedContent[contentId] || false;
    // メモ内容の場合は「さらに表示」ボタンを表示しない
    const needsExpansion = !isMemoContent && content && content.length > 50;
    
    // キャッシュされた完全な内容を取得
    const cachedContent = fullContentCache[contentId];
    const displayContent = cachedContent || content;
    
    const handleExpandClick = async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (postId && content.endsWith('...') && content.length <= 103 && !cachedContent) {
        // 完全な内容を取得してキャッシュに保存
        const fullContent = await getFullContent(postId, content);
        if (fullContent !== content) {
          setFullContentCache(prev => ({
            ...prev,
            [contentId]: fullContent
          }));
        }
      }
      toggleExpanded(contentId);
    };
    
    return (
      <Box>
        <Typography
          variant="body2"
          sx={{
            color: AppColors.textSecondary,
            fontStyle: 'italic',
            wordBreak: 'break-word',
            ...(isMemoContent ? {
              // メモ内容は常に全文表示
              whiteSpace: 'pre-wrap',
              display: 'block',
              overflow: 'visible',
              textOverflow: 'clip',
              maxHeight: 'none',
              height: 'auto',
            } : isExpanded ? {
              // 展開時は全文表示
              whiteSpace: 'pre-wrap',
              display: 'block',
              overflow: 'visible',
              textOverflow: 'clip',
              maxHeight: 'none',
              height: 'auto',
            } : needsExpansion ? {
              // 展開が必要な場合は複数行で省略表示（改行を保持）
              whiteSpace: 'pre-wrap',
              display: 'block',
              overflow: 'hidden',
              maxHeight: '4.5em', // 約3行分の高さ（1.5em × 3）
              lineHeight: '1.5em',
            } : {
              // 短い文章の場合は全文表示（改行を保持）
              whiteSpace: 'pre-wrap',
              display: 'block',
              overflow: 'visible',
              textOverflow: 'clip',
            })
          }}
        >
          {displayContent}
        </Typography>
        {needsExpansion && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              size="small"
              onClick={handleExpandClick}
              sx={{
                color: AppColors.primary,
                fontSize: 13,
                fontWeight: 600,
                textTransform: 'none',
                p: 0,
                minWidth: 'auto',
                border: 'none',
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              {isExpanded ? '表示を減らす' : 'さらに表示'}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  if (authLoading || companyLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: AppColors.surface,
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: AppColors.primary }} />
      </Box>
    );
  }

  return (
    <ResponsiveAdminLayout>
      {/* メインコンテンツ */}
      <Box sx={{ flex: 1 }}>
        {/* 中ヘッダー */}
        <Box sx={{ width: '100%', p: 2, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '24px' }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'black' }}>
              監査ログ
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '24px' }}>
              {(selectedFilter || selectedDate) && (
                <IconButton
                  onClick={() => { setSelectedFilter(null); setSelectedDate(null); }}
                  sx={{ p: 0.5, minWidth: 'auto' }}
                >
                  <ClearIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
              
              <IconButton
                onClick={() => setSelectedFilter(selectedFilter === 'voice' ? null : 'voice')}
                sx={{ p: 0.5, minWidth: 'auto' }}
              >
                <MicIcon sx={{ fontSize: 20, color: selectedFilter === 'voice' ? AppColors.voiceBoxColor : 'black' }} />
              </IconButton>
              
              <IconButton
                onClick={() => setSelectedFilter(selectedFilter === 'thanks' ? null : 'thanks')}
                sx={{ p: 0.5, minWidth: 'auto' }}
              >
                <FavoriteIcon sx={{ fontSize: 20, color: selectedFilter === 'thanks' ? '#FFB6C1' : 'black' }} />
              </IconButton>
              
              <IconButton
                onClick={handleDatePickerOpen}
                sx={{ p: 0.5, minWidth: 'auto' }}
              >
                <SearchIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* 基準の余白の状態: PCは最大幅1000px・中央寄せ */}
        <Box sx={{ maxWidth: isDesktop ? '1000px' : '100%', mx: isDesktop ? 'auto' : 0, width: '100%' }}>
        {/* コンテンツエリア */}
        <Box sx={{ p: 2 }}>

        {/* エラー表示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {/* 監査ログリスト */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : auditLogs.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 8,
                color: AppColors.textTertiary,
              }}
            >
              <AssignmentIcon sx={{ fontSize: 64, mb: 1.5 }} />
              <Typography variant="h6">監査ログがありません</Typography>
            </Box>
          ) : (
            <>
              {paginatedAuditLogs.map((log) => (
              <Card
                key={log.id}
                sx={{
                  mb: 1.5,
                  borderRadius: 3,
                  border: `1px solid ${AppColors.border}`,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Chip
                      label={AuditLogService.getActionLabel(log.action, log.details, company?.industryType)}
                      size="small"
                      sx={{
                        backgroundColor: AuditLogService.getActionColor(log.action, log.details) + '1A',
                        color: AuditLogService.getActionColor(log.action, log.details),
                        fontWeight: 'bold',
                        mr: 2,
                      }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: AppColors.textSecondary,
                        fontSize: 12,
                      }}
                    >
                      {AuditLogService.formatTimestampSmart(log.timestamp)}
                    </Typography>
                  </Box>

                  {/* 説明文の表示（ステータス部分に色をつける） */}
                  <Box sx={{ mb: 1 }}>
                    {buildDescriptionWithColoredStatus(log.description, log.details)}
                  </Box>

                  {/* 投稿内容の表示（ステータス変更の場合のみ） */}
                  {(log.details.actionType === 'status_change' || log.details.actionType === 'visibility_change') && (
                    <Box sx={{ mb: 1 }}>
                      {((log.details as any).postContent || log.details.postContentPreview) && (
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: AppColors.surface,
                            borderRadius: 2,
                            border: `1px solid ${AppColors.border}`,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <DescriptionIcon sx={{ fontSize: 16, color: AppColors.textSecondary, mr: 0.5 }} />
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 'bold',
                                color: AppColors.textSecondary,
                              }}
                            >
                              対応スレッド
                            </Typography>
                          </Box>
                          {buildExpandableContent(
                            (log.details as any).postContent || log.details.postContentPreview || '',
                            `${log.details.postId}_preview`,
                            log.details.postId
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* 投稿内容の表示（メモ投稿の場合） */}
                  {(() => {
                    const isMemoAdded = log.details.actionType === 'memo_added' || log.action === 'memo_added';
                    const hasContent = (log.details as any).postContent || (log.details as any).memoContent;
                    return isMemoAdded && hasContent;
                  })() && (
                    <Box sx={{ mb: 1 }}>
                      <Box
                        component={(log.details as any).postId ? "a" : "div"}
                        href={(log.details as any).postId ? `/admin/${(log.details as any).postType === 'voice' ? 'voice' : (log.details as any).postType === 'thanks' ? 'thanks' : 'post'}/${(log.details as any).postId}` : undefined}
                        onClick={(log.details as any).postId ? (e: React.MouseEvent) => {
                          e.preventDefault();
                          navigate(`/admin/${(log.details as any).postType === 'voice' ? 'voice' : (log.details as any).postType === 'thanks' ? 'thanks' : 'post'}/${(log.details as any).postId}`);
                        } : undefined}
                        sx={{
                          display: 'block',
                          p: 1.5,
                          bgcolor: AppColors.surface,
                          borderRadius: 2,
                          border: `1px solid ${AppColors.border}`,
                          textDecoration: 'none',
                          color: 'inherit',
                          cursor: (log.details as any).postId ? 'pointer' : 'default',
                          transition: 'all 0.2s',
                          '&:hover': (log.details as any).postId ? {
                            bgcolor: AppColors.surface,
                            borderColor: AppColors.primary,
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                          } : {},
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <DescriptionIcon sx={{ fontSize: 16, color: AppColors.textSecondary, mr: 0.5 }} />
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 'bold',
                              color: AppColors.textSecondary,
                            }}
                          >
                            対応スレッド
                          </Typography>
                        </Box>
                        {buildExpandableContent(
                          (log.details as any).postContent || (log.details as any).memoContent || '',
                          `${(log.details as any).postId || 'unknown'}_memo`,
                          (log.details as any).postId
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* ユーザー情報 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ fontSize: 16, color: AppColors.textSecondary, mr: 0.5 }} />
                      <Typography
                        variant="caption"
                        sx={{ color: AppColors.textSecondary }}
                      >
                        {userNames[log.userId] || log.userName}
                      </Typography>
                    </Box>
                    {log.ipAddress && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOnIcon sx={{ fontSize: 16, color: AppColors.textSecondary, mr: 0.5 }} />
                        <Typography
                          variant="caption"
                          sx={{ color: AppColors.textSecondary }}
                        >
                          {log.ipAddress}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
            </>
          )}
          
          {/* ページネーション */}
          {totalPages > 1 && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 2,
              py: 3,
              pt: 2,
            }}>
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                  mb: 1,
                }}
              >
                {auditLogs.length}件中 {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, auditLogs.length)}件を表示
              </Typography>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                disabled={loading}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* 日付ピッカーダイアログ */}
      <Dialog
        open={datePickerOpen}
        onClose={handleDatePickerClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>日付を選択</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <input
              type="date"
              value={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                if (e.target.value) {
                  handleDateChange(new Date(e.target.value));
                } else {
                  handleDateChange(null);
                }
              }}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDatePickerClose}>キャンセル</Button>
          <Button onClick={() => handleDateChange(null)}>クリア</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </ResponsiveAdminLayout>
  );
};